import axios from "axios";
import { API_BASE_URL, CUSTOMER_API_BASE_URL, NODE_API_BASE_URL, PUBLIC_NODE_API_BASE_URL, PAYMENT_API_BASE_URL, isLocalhost } from "../utils";
import { authHeaders, clearCustomerSession, getCustomerAccessToken } from "../auth/customerSession";
import {
  requestHasRawProfileUrl,
  SELF_CUSTOMER_ID_FIELDS,
  stripCustomerIdentityFields,
  USER_TARGET_ID_FIELDS,
  isRawNumericId,
} from "../auth/customerIdentity";
import { resolveAskWittyNodeBaseUrl } from "./askWittyTransport";

export const ASKWITTY_NODE_API_BASE_URL = resolveAskWittyNodeBaseUrl({
  hostname: typeof window === "undefined" ? "" : window.location.hostname,
  nodeApiBaseUrl: NODE_API_BASE_URL,
  proxyPath: process.env.REACT_APP_ASKWITTY_NODE_PROXY_PATH,
});

// Localhost dev only: the customer session is an HttpOnly `.partywitty.com` cookie
// that Chrome blocks as third-party from localhost:3000. Rewrite every absolute
// partywitty API host to the same-origin CRA dev proxy (see setupProxy.js) so the
// cookie is first-party and gets sent. This is the SINGLE choke point that covers
// all axios + fetch calls, regardless of which base URL the call site used.
// NOTE: only HTTP API traffic flows through here — <img>/<a href> assets are not
// intercepted, so they keep loading cross-origin and are unaffected.
const PROXY_HOST_MAP = [
  { re: /^https?:\/\/dashboard\.partywitty\.com/i, prefix: "/__pw_api" },
  { re: /^https?:\/\/vpnnode\.partywitty\.com/i, prefix: "/__pw_node" },
];

function toLocalProxyUrl(rawUrl) {
  if (!isLocalhost) return rawUrl;
  const value = String(rawUrl || "");
  for (const { re, prefix } of PROXY_HOST_MAP) {
    if (re.test(value)) return value.replace(re, prefix);
  }
  return rawUrl;
}

function getResolvedRequestUrl(config = {}) {
  const baseURL = String(config.baseURL || "").trim();
  const url = String(config.url || "").trim();
  try {
    return new URL(url, baseURL || window.location.origin).toString();
  } catch {
    return `${baseURL}${url}`;
  }
}

function isPartywittyCustomerUrl(url) {
  const value = String(url || "");
  const startsWithConfiguredBase = (base) => Boolean(String(base || "").trim())
    && value.startsWith(String(base).trim());
  return (
    startsWithConfiguredBase(API_BASE_URL) ||
    startsWithConfiguredBase(NODE_API_BASE_URL) ||
    startsWithConfiguredBase(PAYMENT_API_BASE_URL) ||
    startsWithConfiguredBase(ASKWITTY_NODE_API_BASE_URL) ||
    value.startsWith(API_BASE_URL) ||
    value.startsWith(NODE_API_BASE_URL) ||
    value.startsWith(ASKWITTY_NODE_API_BASE_URL) ||
    value.includes("/__pw_api") ||
    value.includes("/__pw_node") ||
    value.includes("/__pw_gateway") ||
    /^https:\/\/(admin|dashboard|node|vpnnode|api)\.partywitty\.com\//i.test(value)
  );
}

function sanitizeRequestUrl(url) {
  const rawUrl = String(url || "");
  if (!rawUrl) return rawUrl;
  const isAbsolute = /^https?:\/\//i.test(rawUrl);
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    SELF_CUSTOMER_ID_FIELDS.forEach((key) => parsed.searchParams.delete(key));
    USER_TARGET_ID_FIELDS.forEach((key) => {
      if (isRawNumericId(parsed.searchParams.get(key))) {
        parsed.searchParams.delete(key);
      }
    });
    if (!isAbsolute) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return parsed.toString();
  } catch {
    return rawUrl;
  }
}

export function applyCustomerAuth(config = {}) {
  const originalResolved = getResolvedRequestUrl(config);
  if (requestHasRawProfileUrl(originalResolved)) {
    return Promise.reject(new Error("Raw customer profile IDs are blocked"));
  }

  // Localhost: rewrite an absolute partywitty API host to the same-origin dev
  // proxy so the session cookie is sent first-party. Done before auth/sanitize
  // so the rest of the pipeline operates on the proxied (same-origin) URL.
  let workingConfig = config;
  if (isLocalhost) {
    const proxiedUrl = toLocalProxyUrl(originalResolved);
    if (proxiedUrl !== originalResolved) {
      workingConfig = { ...config, url: proxiedUrl, baseURL: "" };
    }
  }

  // Protect if the original (absolute) OR rewritten (proxy) URL is a customer URL.
  const shouldProtect =
    isPartywittyCustomerUrl(originalResolved) ||
    isPartywittyCustomerUrl(getResolvedRequestUrl(workingConfig));
  // Opt-in escape hatch for endpoints that still require the caller's own
  // identifier in the request body (e.g. legacy lead/enquiry submit endpoints
  // that have not been migrated to resolve the user from the token/public_id).
  // Only self identity fields are preserved; target ids stay protected.
  const allowSelfIdentity = workingConfig.allowSelfIdentity === true;
  const protectedConfig = shouldProtect
    ? {
        ...workingConfig,
        url: sanitizeRequestUrl(workingConfig.url),
        params: allowSelfIdentity
          ? workingConfig.params
          : stripCustomerIdentityFields(workingConfig.params),
        data: allowSelfIdentity
          ? workingConfig.data
          : stripCustomerIdentityFields(workingConfig.data),
      }
    : { ...workingConfig };

  const token = shouldProtect ? getCustomerAccessToken() : "";
  const headers = protectedConfig.headers || {};
  return {
    ...protectedConfig,
    withCredentials: shouldProtect ? true : protectedConfig.withCredentials,
    headers: token
      ? {
          ...headers,
          Authorization: `Bearer ${token}`,
        }
      : headers,
  };
}

export const customerApiClient = axios.create({
  baseURL: CUSTOMER_API_BASE_URL,
  withCredentials: true,
});

export const customerNodeClient = axios.create({
  baseURL: NODE_API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Public reads such as the guest Discover deck must not depend on a local
// Node process during localhost development. This client is deliberately used
// only by endpoints that are safe without authentication.
export const publicNodeClient = axios.create({
  baseURL: PUBLIC_NODE_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Ask Witty is served through a same-origin reverse proxy on localhost and the
// VPN-only dev site. Shared proxy credentials stay on that server; the browser
// sends only its normal customer/guest session credentials. Customer production
// hosts retain the existing direct Node API base URL.
export const askWittyNodeClient = axios.create({
  baseURL: ASKWITTY_NODE_API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

customerApiClient.interceptors.request.use(applyCustomerAuth);
customerNodeClient.interceptors.request.use(applyCustomerAuth);
askWittyNodeClient.interceptors.request.use(applyCustomerAuth);
axios.interceptors.request.use(applyCustomerAuth);

export const CUSTOMER_SESSION_EXPIRED_EVENT = "pw-customer-session-expired";

const AUTH_FLOW_URL_PATTERN = /\/Api\/UserAuth\/|\/APIs\/AuthUser\/|verifyOtp|sendOtp|loginOtp/i;

let lastSessionExpiredNotifyAt = 0;

function hasStoredCustomerSession() {
  if (getCustomerAccessToken()) return true;
  try {
    const userData = JSON.parse(localStorage.getItem("userData") || "null");
    return Boolean(
      userData && (userData.public_id || userData.id || userData[0]?.public_id || userData[0]?.id),
    );
  } catch {
    return false;
  }
}

// Authed customer GET used only to re-check whether the session is really dead.
const SESSION_PROBE_PATH = "/APIs/RewardWallet/getCarrotHistory";
let sessionVerifyInFlight = false;

// Exported so a save-flow catch block that already knows its own request came
// back 401 can clear the session immediately instead of waiting on the
// debounced cross-service probe below (which exists for incidental/background
// 401s, not for a 401 on the exact request the user just made).
export function expireCustomerSession(requestUrl) {
  clearCustomerSession();
  localStorage.removeItem("userData");
  localStorage.removeItem("activeProfileRole");
  localStorage.removeItem("pw_profile_context");
  localStorage.removeItem("discoverProfileVerified");
  localStorage.removeItem("discoverFlowState");
  localStorage.removeItem("discoverLastActiveLocationSyncedAt");
  const now = Date.now();
  if (now - lastSessionExpiredNotifyAt < 4000) return;
  lastSessionExpiredNotifyAt = now;
  window.dispatchEvent(
    new CustomEvent(CUSTOMER_SESSION_EXPIRED_EVENT, { detail: { url: String(requestUrl || "") } }),
  );
}

// A single 401 can come from a secondary endpoint that doesn't share the customer
// session (e.g. a remote Node/admin service in local dev). Before tearing down the
// session and popping "Session Expired", re-probe the PRIMARY customer API — if it
// still responds, the user is genuinely logged in and this was a false alarm.
// The in-flight flag also breaks recursion: the probe's own 401 re-enters here and
// returns early instead of looping.
export function notifyCustomerSessionExpired(requestUrl = "") {
  if (!isPartywittyCustomerUrl(requestUrl)) return;
  if (AUTH_FLOW_URL_PATTERN.test(String(requestUrl || ""))) return;
  if (!hasStoredCustomerSession()) return;
  if (sessionVerifyInFlight) return;
  sessionVerifyInFlight = true;
  fetch(`${CUSTOMER_API_BASE_URL}${SESSION_PROBE_PATH}`, {
    credentials: "include",
    headers: authHeaders(),
  })
    .then((res) => {
      if (res.status === 401) expireCustomerSession(String(requestUrl || ""));
    })
    .catch(() => {
      // Network error probing the session → don't log the user out on a guess.
    })
    .finally(() => {
      sessionVerifyInFlight = false;
    });
}

function handleUnauthorizedResponse(error) {
  const status = Number(error?.response?.status || 0);
  if (status === 401) {
    notifyCustomerSessionExpired(getResolvedRequestUrl(error?.config || {}));
  }
  return Promise.reject(error);
}

customerApiClient.interceptors.response.use((response) => response, handleUnauthorizedResponse);
customerNodeClient.interceptors.response.use((response) => response, handleUnauthorizedResponse);
askWittyNodeClient.interceptors.response.use((response) => response, handleUnauthorizedResponse);
axios.interceptors.response.use((response) => response, handleUnauthorizedResponse);

function installFetchGuard() {
  if (typeof window === "undefined" || typeof window.fetch !== "function" || window.__pwCustomerFetchGuardInstalled) {
    return;
  }
  const nativeFetch = window.fetch.bind(window);
  window.__pwCustomerFetchGuardInstalled = true;
  window.fetch = (input, init = {}) => {
    const rawUrl = typeof input === "string" ? input : input?.url;
    const resolvedUrl = (() => {
      try {
        return new URL(rawUrl || "", window.location.origin).toString();
      } catch {
        return String(rawUrl || "");
      }
    })();
    if (requestHasRawProfileUrl(resolvedUrl)) {
      return Promise.reject(new Error("Raw customer profile IDs are blocked"));
    }
    if (!isPartywittyCustomerUrl(resolvedUrl)) {
      return nativeFetch(input, init);
    }
    const nextInit = {
      ...init,
      credentials: "include",
      headers: authHeaders(init.headers || {}),
      body: stripCustomerIdentityFields(init.body),
    };
    // Localhost: route absolute partywitty API URLs through the same-origin dev
    // proxy so the cookie is first-party (string inputs only; Request objects pass through).
    const nextInput = typeof input === "string"
      ? sanitizeRequestUrl(isLocalhost ? toLocalProxyUrl(input) : input)
      : input;
    return nativeFetch(nextInput, nextInit).then((response) => {
      if (response && Number(response.status) === 401) {
        notifyCustomerSessionExpired(resolvedUrl);
      }
      return response;
    });
  };
}

installFetchGuard();

export function customerFetch(url, options = {}) {
  if (requestHasRawProfileUrl(url)) {
    return Promise.reject(new Error("Raw customer profile IDs are blocked"));
  }
  const body = stripCustomerIdentityFields(options.body);
  return fetch(sanitizeRequestUrl(url), {
    ...options,
    credentials: "include",
    headers: authHeaders(options.headers || {}),
    body,
  });
}

export const PROFILE_CONTEXT_STORAGE_KEY = "pw_profile_context";

function getPayload(response) {
  return response?.data || response || {};
}

export function readStoredProfileContext() {
  try {
    return JSON.parse(localStorage.getItem(PROFILE_CONTEXT_STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

export function storeProfileContext(context) {
  if (!context || typeof context !== "object") return null;
  localStorage.setItem(PROFILE_CONTEXT_STORAGE_KEY, JSON.stringify(context));
  if (context.current_active_profile_type) {
    localStorage.setItem("activeProfileRole", context.current_active_profile_type);
  }
  window.dispatchEvent(new CustomEvent("pw-profile-context-changed", { detail: context }));
  return context;
}

export function clearStoredProfileContext() {
  localStorage.removeItem(PROFILE_CONTEXT_STORAGE_KEY);
}

export async function fetchProfileContext() {
  const response = await customerNodeClient.get("/profile/context");
  const payload = getPayload(response);
  const context = payload.data || payload;
  storeProfileContext(context);
  return payload;
}

export async function switchProfile(profileType) {
  const response = await customerNodeClient.post("/profile/switch", { profile_type: profileType });
  const payload = getPayload(response);
  const context = payload.data || payload;
  storeProfileContext(context);
  return payload;
}

export async function fetchProfileEditOptions() {
  const response = await customerNodeClient.get("/profile/edit-options");
  return getPayload(response);
}

export async function saveProfilePreferences(payload = {}) {
  const response = await customerNodeClient.post("/profile/preferences", payload);
  return getPayload(response);
}

export async function setArtistAvailability(available) {
  const response = await customerNodeClient.post("/profile/artist/availability", { available_as_artist: Boolean(available) });
  const payload = getPayload(response);
  const context = payload.data || payload;
  storeProfileContext(context);
  return payload;
}

export async function fetchArtistAvailability() {
  const response = await customerNodeClient.get("/profile/artist/availability");
  return getPayload(response);
}

export async function fetchArtistProfile() {
  const response = await customerNodeClient.get("/profile/artist");
  const payload = getPayload(response);
  if (payload.data?.context) {
    storeProfileContext(payload.data.context);
  }
  return payload;
}

export async function fetchArtistOpportunity(token) {
  const response = await publicNodeClient.get(
    `/artist/opportunities/${encodeURIComponent(String(token || "").trim())}`,
  );
  return getPayload(response);
}

export async function submitArtistOpportunityQuote(token, amount) {
  const response = await customerNodeClient.post(
    `/artist/opportunities/${encodeURIComponent(String(token || "").trim())}/quote`,
    { amount: String(amount || "").trim() },
  );
  return getPayload(response);
}

export async function saveArtistProfile(payload) {
  const response = await customerNodeClient.post("/profile/artist", payload || {});
  const result = getPayload(response);
  if (result.data?.context) {
    storeProfileContext(result.data.context);
  }
  return result;
}

export async function fetchArtistTaxonomy() {
  const response = await customerNodeClient.get("/artist/taxonomy");
  return getPayload(response);
}

export async function fetchArtistFacetSelection() {
  const response = await customerNodeClient.get("/profile/artist/facets");
  return getPayload(response);
}

export async function saveArtistFacetSelection(payload) {
  const response = await customerNodeClient.post("/profile/artist/facets", payload || {});
  return getPayload(response);
}

export async function fetchArtistReferralCandidates(query = "") {
  const suffix = String(query || "").trim() ? `?query=${encodeURIComponent(String(query).trim())}` : "";
  const response = await customerNodeClient.get(`/artist/referral/candidates${suffix}`);
  return getPayload(response);
}

export async function fetchArtistReferralRequests() {
  const response = await customerNodeClient.get("/artist/referral/requests");
  return getPayload(response);
}

export async function requestArtistReferral(payload) {
  const response = await customerNodeClient.post("/artist/referral/request", payload || {});
  return getPayload(response);
}

export async function acceptArtistReferralRequest(requestId) {
  const response = await customerNodeClient.post(`/artist/referral/requests/${encodeURIComponent(requestId)}/accept`);
  return getPayload(response);
}

export async function rejectArtistReferralRequest(requestId, note = "") {
  const response = await customerNodeClient.post(
    `/artist/referral/requests/${encodeURIComponent(requestId)}/reject`,
    { note },
  );
  return getPayload(response);
}

export async function generateArtistReferralCode() {
  const response = await customerNodeClient.post("/artist/referral/code");
  return getPayload(response);
}

export async function validateArtistActivationCode(code) {
  const response = await customerNodeClient.post("/artist/activation/validate", { code });
  return getPayload(response);
}

export async function redeemArtistActivationCode(code) {
  const response = await customerNodeClient.post("/artist/activation/redeem", { code });
  return getPayload(response);
}

export async function fetchArtistFamilyTree() {
  const response = await customerNodeClient.get("/artist/family-tree");
  return getPayload(response);
}

export async function uploadArtistFile(file, payload = {}) {
  const formData = new FormData();
  formData.append("file", file);
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });
  const response = await customerNodeClient.post("/profile/artist/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return getPayload(response);
}

export async function requestArtistGoLive(payload = {}) {
  const response = await customerNodeClient.post("/profile/artist/go-live/request", payload);
  const result = getPayload(response);
  if (result.data?.context) {
    storeProfileContext(result.data.context);
  }
  return result;
}

export async function fetchArtistContentWorkspace() {
  const response = await customerNodeClient.get("/profile/artist/content");
  return getPayload(response);
}

export async function beginArtistAccountConnection(provider, payload = {}) {
  const response = await customerNodeClient.post(
    `/profile/artist/accounts/${encodeURIComponent(provider)}/authorize`,
    payload,
  );
  return getPayload(response);
}

export async function syncArtistLinkedAccount(provider) {
  const response = await customerNodeClient.post(
    `/profile/artist/accounts/${encodeURIComponent(provider)}/sync`,
  );
  return getPayload(response);
}

export async function disconnectArtistLinkedAccount(provider) {
  const response = await customerNodeClient.delete(
    `/profile/artist/accounts/${encodeURIComponent(provider)}`,
  );
  return getPayload(response);
}

export async function classifyArtistCatalogueItem(itemId, contentType) {
  const response = await customerNodeClient.patch(
    `/profile/artist/catalogue/${encodeURIComponent(itemId)}`,
    { content_type: contentType },
  );
  return getPayload(response);
}

export async function setArtistContentPublicationConsent(itemId, granted, widgetId) {
  const response = await customerNodeClient.post(
    `/profile/artist/catalogue/${encodeURIComponent(itemId)}/consent`,
    { granted, widget_id: widgetId },
  );
  return getPayload(response);
}

export async function fetchArtistPricing() {
  const response = await customerNodeClient.get("/profile/artist/pricing");
  return getPayload(response);
}

export async function saveArtistPricing(payload = {}) {
  const response = await customerNodeClient.post("/profile/artist/pricing", payload);
  return getPayload(response);
}

export async function fetchArtistAreas() {
  const response = await customerNodeClient.get("/profile/artist/areas");
  return getPayload(response);
}

export async function saveArtistArea(payload = {}) {
  const response = await customerNodeClient.post("/profile/artist/areas", payload);
  return getPayload(response);
}

export async function removeArtistArea(areaId) {
  const response = await customerNodeClient.delete(`/profile/artist/areas/${encodeURIComponent(areaId)}`);
  return getPayload(response);
}

export async function fetchProfileDashboard() {
  const response = await customerNodeClient.get("/profile/dashboard");
  const payload = getPayload(response);
  if (payload.data?.context) {
    storeProfileContext(payload.data.context);
  }
  return payload;
}

export async function fetchArtistLeads() {
  const response = await customerNodeClient.get("/artist/leads");
  return getPayload(response);
}

export async function submitArtistBid(leadId, amount) {
  const response = await customerNodeClient.post(`/artist/leads/${encodeURIComponent(leadId)}/bid`, { amount });
  return getPayload(response);
}

export async function declineArtistLead(leadId) {
  const response = await customerNodeClient.post(`/artist/leads/${encodeURIComponent(leadId)}/decline`);
  return getPayload(response);
}

export async function fetchArtistDemandInsight(filters = {}) {
  const params = new URLSearchParams();
  ["category", "area", "window"].forEach((key) => {
    const value = String(filters[key] || "").trim();
    if (value) params.set(key, value);
  });
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const response = await customerNodeClient.get(`/demand/insight${suffix}`);
  return getPayload(response);
}
